import { useEffect, useMemo, useRef } from 'react';

import library from '../../lib/graph-library.json';
import { docIO } from '../../lib/doc-io.js';
import { newGraphDoc, useGraph, type GraphDoc, type GraphKind } from '../../state/graph.js';
import { useWorkspace } from '../../state/workspace.js';
import { GraphEditor, type NodeDef } from './GraphEditor.js';
import './graph.css';

/**
 * Tool panel hosting the Visual Script (`*.vgraph.json`) and Shader Graph
 * (`*.shadergraph.json`) editors. Open a graph from the file tree (openAsset)
 * or create one with the buttons shown while nothing is open.
 */

const LIBS = library as unknown as Record<GraphKind, NodeDef[]>;

export const GRAPH_PANEL = 'graph';

export async function createGraph(kind: GraphKind, name: string): Promise<string | null> {
  const pid = useWorkspace.getState().projectId;
  if (!pid) return null;
  const path = kind === 'vgraph' ? `scripts/${name}.vgraph.json` : `shaders/${name}.shadergraph.json`;
  await docIO().write(pid, path, `${JSON.stringify(newGraphDoc(kind, name), null, 2)}\n`);
  await useGraph.getState().open(path);
  return path;
}

export function GraphPanel() {
  const { path, kind, doc, error, saveState, diagnostics, compiled, commit, undo, redo } = useGraph();

  const errors = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of diagnostics) if (d.node) m.set(d.node, d.message);
    return m;
  }, [diagnostics]);

  if (!path || !kind || !doc) {
    return (
      <div className="gp-root" style={{ padding: 12, gap: 8, fontSize: 12 }}>
        {error ? <span className="gp-diag">{error}</span> : <span className="muted">Open a *.vgraph.json or *.shadergraph.json file, or create one:</span>}
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => void createGraph('vgraph', 'new_script')}>New Visual Script</button>
          <button onClick={() => void createGraph('shadergraph', 'new_shader')}>New Shader Graph</button>
        </div>
      </div>
    );
  }

  return (
    <div className="gp-root">
      <div className="gp-bar">
        <strong>{path}</strong>
        <span>{saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Save failed' : 'Saving…'}</span>
        {compiled === true && <span className="gp-ok">Compiled{kind === 'shadergraph' ? ' → .wgsl' : ''}</span>}
        {compiled === false && <span className="gp-diag">{diagnostics.length} error(s)</span>}
        <button onClick={undo}>Undo</button>
        <button onClick={redo}>Redo</button>
      </div>
      <div className="gp-body">
        <GraphEditor doc={doc} defs={LIBS[kind]} errors={errors} onChange={commit} onUndo={undo} onRedo={redo} />
        <div className="gp-side">
          <Variables doc={doc} kind={kind} onChange={commit} />
          {kind === 'shadergraph' && <Preview doc={doc} />}
          {diagnostics.map((d, i) => (
            <div key={i} className="gp-diag">
              {d.node ? `[${d.node}] ` : ''}
              {d.message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Variables({ doc, kind, onChange }: { doc: GraphDoc; kind: GraphKind; onChange: (d: GraphDoc) => void }) {
  const types = kind === 'shadergraph' ? ['f32', 'vec2', 'vec3', 'vec4', 'color', 'texture'] : ['number', 'string', 'bool', 'map'];
  const set = (i: number, patch: Partial<GraphDoc['variables'][number]>) => onChange({ ...doc, variables: doc.variables.map((v, j) => (j === i ? { ...v, ...patch } : v)) });
  return (
    <div>
      <strong>{kind === 'shadergraph' ? 'Properties' : 'Variables'}</strong>
      {doc.variables.map((v, i) => (
        <div key={i} style={{ display: 'flex', gap: 3, margin: '3px 0' }}>
          <input style={{ width: 70 }} value={v.name} onChange={(e) => set(i, { name: e.target.value })} />
          <select value={v.type} onChange={(e) => set(i, { type: e.target.value })}>
            {types.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
          {v.type !== 'texture' && (
            <input
              style={{ width: 50 }}
              value={JSON.stringify(v.default ?? '')}
              onChange={(e) => {
                try {
                  set(i, { default: JSON.parse(e.target.value) });
                } catch {
                  /* keep typing */
                }
              }}
            />
          )}
          <button onClick={() => onChange({ ...doc, variables: doc.variables.filter((_, j) => j !== i) })}>×</button>
        </div>
      ))}
      <button onClick={() => onChange({ ...doc, variables: [...doc.variables, { name: `v${doc.variables.length + 1}`, type: types[0]!, default: kind === 'shadergraph' ? 0 : null }] })}>Add</button>
    </div>
  );
}

/** Flat colour a master input resolves to when it is a constant, property default or literal. */
function constColor(doc: GraphDoc, pin: string, fallback: number[]): number[] {
  const master = doc.nodes.find((n) => n.type.startsWith('master_'));
  if (!master) return fallback;
  const edge = doc.edges.find((e) => e.to === `${master.id}.${pin}`);
  const src = edge && doc.nodes.find((n) => n.id === edge.from.split('.')[0]);
  let v: unknown = master.data[pin];
  if (src?.type === 'property') v = doc.variables.find((x) => x.name === src.data.name)?.default;
  else if (src?.type === 'float' || src?.type === 'vector') v = src.data.value;
  else if (src) return fallback;
  if (typeof v === 'number') return [v, v, v];
  return Array.isArray(v) && v.length >= 3 ? (v as number[]).slice(0, 3) : fallback;
}

/** Preview sphere: lambert + specular shading of the master's constant inputs (the real WGSL runs in the engine viewport). */
function Preview({ doc }: { doc: GraphDoc }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const c = ref.current;
    const g = c?.getContext('2d');
    if (!c || !g) return;
    const base = constColor(doc, 'base_color', [0.8, 0.8, 0.8]);
    const emit = constColor(doc, 'emission', [0, 0, 0]);
    const img = g.createImageData(c.width, c.height);
    const r = c.width / 2 - 4;
    for (let y = 0; y < c.height; y++) {
      for (let x = 0; x < c.width; x++) {
        const nx = (x - c.width / 2) / r, ny = -(y - c.height / 2) / r;
        const d = nx * nx + ny * ny;
        const i = (y * c.width + x) * 4;
        if (d > 1) {
          img.data[i + 3] = 0;
          continue;
        }
        const nz = Math.sqrt(1 - d);
        const l = Math.max(0, (nx * -0.4 + ny * 0.6 + nz * 0.7) / 0.94);
        const spec = Math.pow(Math.max(0, nz * 0.7 + ny * 0.6 - nx * 0.4), 24) * 0.3;
        for (let k = 0; k < 3; k++) {
          const v = base[k]! * (0.15 + 0.85 * l) + spec + emit[k]!;
          img.data[i + k] = Math.round(255 * Math.pow(Math.min(1, v), 1 / 2.2));
        }
        img.data[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
  }, [doc]);
  return (
    <div>
      <strong>Preview</strong>
      <canvas ref={ref} width={160} height={160} style={{ display: 'block' }} />
    </div>
  );
}
